import React, { useState, useEffect } from 'react';

import { CircularProgress } from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import MeshDonutChart from 'components/graph/MeshDonutChart';
import { useDispatch } from 'react-redux';
import { ErrorMessage } from 'screens/analytics/commonComponents';
import styled from 'styled-components';
import {
  NewStyledIconButton,
  StyledBox,
  StyledDivider,
  StyledGrid,
  StyledTypography,
  StyledBorderedPaper,
} from 'styles/common';

import { GOAL_PROGRESSES } from 'constants/ProjectConstants';
import { getColorFromKeyForAnalytics } from 'constants/helper';

import { getObjectiveStatus } from 'services/analytics/goal';

import { AbsCenterContainer, NoDataContainer } from '../styles';
import { useGoalAnalytics } from './GoalAnalyticsContext';

/**
 * @deprecated 06-08-2024
 */
const GoalStatusChart = ({ parentUuid }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [progressType, setProgressType] = useState('MAX');
  const dispatch = useDispatch();

  const {
    state: { currentState },
  } = useGoalAnalytics();

  const { selectedCohort, selectedGroupByKey, selectedSplitByKey, selectedTimeBoundPeriods } = currentState;

  const fetchData = () => {
    setLoading(true);
    getObjectiveStatus({
      parentUuid,
      cohort: selectedCohort,
      groupByKeys: selectedGroupByKey,
      splitByKeys: selectedSplitByKey,
      timeBounds: selectedTimeBoundPeriods,
    })
      .then((res) => {
        console.log(res);
        setData(res);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (selectedCohort === null && selectedGroupByKey === null && selectedSplitByKey === null) return;
    fetchData();
  }, [selectedCohort, selectedGroupByKey, selectedSplitByKey, selectedTimeBoundPeriods]);

  return (
    <StyledBorderedPaper
      style={{
        position: 'relative',
        minHeight: '140px',
        height: '100%',
      }}
    >
      {loading ? (
        <Loader />
      ) : data === null ? (
        <ErrorMessage errorText="Some error occured while fetching results." />
      ) : Object.keys(data).length === 0 ? (
        <NoData />
      ) : (
        <SubmissionStatusCardView data={data} />
      )}
    </StyledBorderedPaper>
  );
};

const SubmissionStatusCardView = ({ data }) => {
  const title = 'Goal Status';
  const description = 'Summary of all current goals';

  const chartData = GOAL_PROGRESSES.map(({ value, label, color }) => ({
    count: data[value]?.COUNT,
    percentage: data[value]?.COMPLETED_PERCENTAGE,
    color: getColorFromKeyForAnalytics(color),
    displayName: label,
  }));

  return (
    <>
      <FixHeightStyledGrid container alignItems="center">
        <StyledGrid item xs={3}>
          <MeshDonutChart data={chartData} />
        </StyledGrid>
        <StyledGrid item flex={9}>
          <StyledTypography variant="h6">{title}</StyledTypography>
          <StyledTypography variant="body1" color="textSecondary">
            {description}
          </StyledTypography>
        </StyledGrid>
      </FixHeightStyledGrid>
      <StyledDivider leftGutter={3} rightGutter={3} />
      <StyledBox leftGutter={3} rightGutter={3} topGutter={3} paddingBottom="1rem">
        {chartData.map(({ color, displayName, percentage, count }) => (
          <StyledGrid container>
            <StyledGrid container alignItems="center" item xs={8} bottomGutter={1}>
              <NewStyledIconButton size="header" color={color} style={{ pointerEvents: 'none' }}>
                <FiberManualRecordIcon />
              </NewStyledIconButton>
              <StyledTypography leftGutter={2} variant="body1" color="textSecondary">
                {displayName}
              </StyledTypography>
            </StyledGrid>
            <StyledGrid item xs={2} bottomGutter={1}>
              <StyledTypography variant="body1" weight={600}>
                {count}
              </StyledTypography>
            </StyledGrid>
            <StyledGrid item xs={2} bottomGutter={1}>
              <StyledTypography variant="body1" color="textSecondary">
                {`${percentage}%`}
              </StyledTypography>
            </StyledGrid>
          </StyledGrid>
        ))}
      </StyledBox>
    </>
  );
};

const Loader = () => (
  <AbsCenterContainer>
    <CircularProgress />
  </AbsCenterContainer>
);

const NoData = () => (
  <AbsCenterContainer>
    <NoDataContainer>Data not found</NoDataContainer>
  </AbsCenterContainer>
);

const FixHeightStyledGrid = styled(StyledGrid)`
  padding: 1.5rem;

  min-height: 10vw;
  ${(props) => props.theme.breakpoints.up('xl')} {
    min-height: 8vw;
  }
`;

export default GoalStatusChart;
